/**
 * 360° equirectangular video VR tour.
 * - Primary: krpano (videoplayer.js) for WebVR, drag, and class view sync.
 * - Fallback: Three.js + video texture if krpano fails to start.
 * - Live class: host overlay + Direct class to my view (orientation + playhead).
 */

import { OrbitControls, useVideoTexture } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { ArrowLeft, Glasses, Play, Square, Video, Volume2, VolumeX, X } from 'lucide-react';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { getVr360TourById, type Vr360Player, type Vr360TourItem } from '../config/vr360Tours';
import { useAuth } from '../contexts/AuthContext';
import { useClassSession } from '../contexts/ClassSessionContext';
import {
  reportSessionProgress,
  reportStudentView,
  subscribeSession,
  updateTeacherView,
} from '../services/classSessionService';
import { getStorageSafely } from '../utils/firebaseStorage';
import { buildKrpano360VideoXml } from '../lib/krpano/buildKrpano360VideoXml';
import { loadKrpanoScript, embedKrpano } from '../lib/krpano/embedKrpano';
import { applyTeacherViewToKrpano } from '../lib/krpano/applyTeacherView';
import { Button } from '../Components/ui/button';
import { LiveClassHostOverlay, type HostLookat } from '../Components/classSession/LiveClassHostOverlay';

const VR360_TOUR_KEY = 'learnxr_vr360_tour';
const KRPANO_TARGET_ID = 'vr360-krpano-embed';
const TEACHER_VIEW_MS = 160;

export interface Vr360TourSessionPayload {
  tourId: string;
  title: string;
  videoPath: string;
  videoStoragePath?: string;
  player?: Vr360Player;
  fromClassSession?: boolean;
}

type KrpanoApi = { call: (a: string) => void; get?: (path: string) => unknown };

function readPartnerDemoSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('learnxr_partner_demo_session');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string };
    return typeof parsed?.id === 'string' ? parsed.id : null;
  } catch {
    return null;
  }
}

function readKrpanoVideoTime(k: KrpanoApi | null): number | null {
  if (!k?.get) return null;
  for (const path of ['plugin[video].time', 'plugin[video].getTime()', 'plugin[video].t']) {
    try {
      const raw = k.get(path);
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(n) && n >= 0) return n;
    } catch {
      /* try next */
    }
  }
  return null;
}

function seekKrpanoVideo(k: KrpanoApi | null, timeSec: number, playing?: boolean) {
  if (!k?.call || !Number.isFinite(timeSec)) return;
  try {
    k.call(`set(plugin[video].time,${timeSec});`);
  } catch {
    try {
      k.call(`plugin[video].seekto(${timeSec});`);
    } catch {
      /* ignore */
    }
  }
  if (typeof playing === 'boolean') {
    try {
      k.call(playing ? 'plugin[video].play();' : 'plugin[video].pause();');
    } catch {
      /* ignore */
    }
  }
}

function readLiveLookat(k: KrpanoApi | null, playing: boolean): HostLookat | null {
  if (!k?.get) return null;
  try {
    const h = Number(k.get('view.hlookat'));
    const v = Number(k.get('view.vlookat'));
    const fov = Number(k.get('view.fov'));
    if (!Number.isFinite(h) || !Number.isFinite(v)) return null;
    const videoTime = readKrpanoVideoTime(k);
    return {
      hlookat: h,
      vlookat: v,
      fov: Number.isFinite(fov) ? fov : 90,
      ...(videoTime != null ? { video_time: videoTime } : {}),
      playing,
    };
  } catch {
    return null;
  }
}

function VideoSphere({ url, muted, playing }: { url: string; muted: boolean; playing: boolean }) {
  const texture = useVideoTexture(url, { muted, start: false, crossOrigin: 'anonymous', loop: true, playsInline: true });
  const video = texture.image as HTMLVideoElement;

  useEffect(() => {
    if (playing) {
      const p = video.play();
      if (p !== undefined) p.catch(() => {});
    } else {
      video.pause();
    }
  }, [playing, video]);

  useEffect(() => {
    video.muted = muted;
  }, [muted, video]);

  return (
    <mesh>
      <sphereGeometry args={[500, 64, 64]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} />
    </mesh>
  );
}

function ViewSyncReporter({ sessionId, userUid, enabled }: { sessionId: string; userUid: string; enabled: boolean }) {
  const { camera } = useThree();
  const last = useRef<{ t: number; h: number; v: number; f: number } | null>(null);
  const tick = useRef(0);
  useFrame(() => {
    if (!enabled || !sessionId) return;
    tick.current += 1;
    if (tick.current % 30 !== 0) return;
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const h = (Math.atan2(dir.x, -dir.z) * 180) / Math.PI;
    const v = (Math.asin(Math.max(-1, Math.min(1, dir.y))) * 180) / Math.PI;
    const f = 'fov' in camera && typeof (camera as THREE.PerspectiveCamera).fov === 'number' ? (camera as THREE.PerspectiveCamera).fov : 90;
    const cur = { t: Date.now(), h, v, f };
    const prev = last.current;
    if (prev && Math.abs(prev.h - h) < 0.3 && Math.abs(prev.v - v) < 0.3) return;
    last.current = cur;
    void reportStudentView(sessionId, userUid, { hlookat: h, vlookat: v, fov: f });
  });
  return null;
}

const VR360VideoTourPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const {
    activeSessionId,
    activeSession,
    joinedSessionId,
    joinedSession,
    progressList,
    bindActiveSession,
  } = useClassSession();
  const [tourDrawer, setTourDrawer] = useState<null | 'roster' | 'approvals' | 'preview'>(null);
  const [payload, setPayload] = useState<Vr360TourSessionPayload | null>(null);
  const [mergedTour, setMergedTour] = useState<Vr360TourItem | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [engine, setEngine] = useState<'krpano' | 'three' | 'pending'>('pending');
  const [inKrpanoVr, setInKrpanoVr] = useState(false);
  const [hostLookat, setHostLookat] = useState<HostLookat | null>(null);
  const [sessionSnap, setSessionSnap] = useState(activeSession || joinedSession);
  const krpanoRef = useRef<KrpanoApi | null>(null);
  const lastTeacherSend = useRef(0);
  const lastStudentReport = useRef(0);
  const lastAppliedTeacherView = useRef<{ h: number; v: number; fov: number; syncId: number | null } | null>(null);
  const playingRef = useRef(playing);
  playingRef.current = playing;

  const syncRef = useRef({
    isHost: false,
    isStudent: false,
    classSessionId: null as string | null,
    userId: null as string | null,
  });

  const effectivePlayer: Vr360Player = payload?.player ?? mergedTour?.player ?? 'krpano';

  const storedClassSessionId =
    typeof window !== 'undefined' ? sessionStorage.getItem('learnxr_class_session_id') : null;
  const storedJoinedSessionId =
    typeof window !== 'undefined' ? sessionStorage.getItem('learnxr_joined_session_id') : null;
  const partnerDemoId = readPartnerDemoSessionId();
  // Students arrive with joinedSessionId; hosts with activeSessionId / partner demo.
  const classSessionId =
    activeSessionId ||
    joinedSessionId ||
    partnerDemoId ||
    storedClassSessionId ||
    storedJoinedSessionId;

  // Bind host session into context so roster / overlay stay live
  useEffect(() => {
    if (!classSessionId || !bindActiveSession) return;
    if (activeSessionId !== classSessionId) {
      const role = profile?.role;
      if (role === 'teacher' || role === 'partner' || role === 'admin' || role === 'superadmin' || role === 'principal' || role === 'school') {
        bindActiveSession(classSessionId);
      }
    }
  }, [classSessionId, activeSessionId, bindActiveSession, profile?.role]);

  useEffect(() => {
    // Prefer live context docs when they match this session
    if (activeSessionId === classSessionId && activeSession) {
      setSessionSnap(activeSession);
      return;
    }
    if (joinedSessionId === classSessionId && joinedSession) {
      setSessionSnap(joinedSession);
    }
  }, [activeSession, activeSessionId, joinedSession, joinedSessionId, classSessionId]);

  // Single session subscription for host roster + student teacher_view when context isn't bound
  useEffect(() => {
    if (!classSessionId) return;
    if (
      (activeSessionId === classSessionId && activeSession) ||
      (joinedSessionId === classSessionId && joinedSession)
    ) {
      return;
    }
    return subscribeSession(classSessionId, (session) => {
      setSessionSnap(session);
    });
  }, [classSessionId, activeSessionId, activeSession, joinedSessionId, joinedSession]);

  const isHost = Boolean(
    classSessionId &&
      user?.uid &&
      (sessionSnap?.teacher_uid === user.uid ||
        (profile?.role === 'partner' &&
          (sessionSnap?.hosted_by_partner === true || Boolean(partnerDemoId))) ||
        (profile?.role === 'teacher' &&
          Boolean(activeSessionId === classSessionId || storedClassSessionId === classSessionId)))
  );
  const isStudent = Boolean(
    classSessionId &&
      user?.uid &&
      !isHost &&
      sessionSnap?.teacher_uid !== user.uid &&
      (profile?.role === 'student' || Boolean(joinedSessionId || storedJoinedSessionId))
  );
  const reportEnabled = isStudent;
  const showHostOverlay = Boolean(isHost && !inKrpanoVr && classSessionId);

  useEffect(() => {
    const fromState = (location.state as { vr360?: Vr360TourSessionPayload } | null)?.vr360;
    if (fromState?.tourId && (fromState.videoPath || fromState.videoStoragePath)) {
      const tour = getVr360TourById(fromState.tourId);
      setMergedTour(tour ?? null);
      setPayload({ ...fromState, videoPath: fromState.videoPath || tour?.videoPath || '' });
      try {
        sessionStorage.setItem(VR360_TOUR_KEY, JSON.stringify({ ...fromState, fromClassSession: fromState.fromClassSession }));
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const raw = sessionStorage.getItem(VR360_TOUR_KEY);
      if (raw) {
        const p = JSON.parse(raw) as Vr360TourSessionPayload;
        if (p.tourId) {
          const tour = getVr360TourById(p.tourId);
          setMergedTour(tour ?? null);
          if (p.videoPath || p.videoStoragePath || tour) {
            setPayload({
              ...p,
              videoPath: p.videoPath || tour?.videoPath || '',
            });
            return;
          }
        }
      }
    } catch {
      /* ignore */
    }
    const tourParam = searchParams.get('tour') || searchParams.get('id');
    if (tourParam) {
      const t = getVr360TourById(tourParam);
      if (t) {
        setMergedTour(t);
        setPayload({
          tourId: t.id,
          title: t.title,
          videoPath: t.videoPath,
          videoStoragePath: t.videoStoragePath,
          player: t.player,
          fromClassSession: false,
        });
        return;
      }
    }
    setLoadError('No video tour selected. Return to Lessons and choose a 360° tour.');
  }, [location.state, searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!payload) return;
      setUrlError(null);
      if (payload.videoPath?.startsWith('http')) {
        if (!cancelled) setResolvedUrl(payload.videoPath);
        return;
      }
      if (payload.videoStoragePath || mergedTour?.videoStoragePath) {
        const stPath = payload.videoStoragePath || mergedTour?.videoStoragePath;
        if (stPath) {
          const st = await getStorageSafely();
          if (cancelled) return;
          if (!st) {
            setUrlError('Storage not ready');
            if (payload.videoPath) if (!cancelled) setResolvedUrl(new URL(payload.videoPath, window.location.origin).href);
            return;
          }
          try {
            const url = await getDownloadURL(storageRef(st, stPath));
            if (!cancelled) setResolvedUrl(url);
            return;
          } catch (e) {
            console.warn('VR360: getDownloadURL failed, using local path if any', e);
            if (payload.videoPath) {
              if (!cancelled) setResolvedUrl(new URL(payload.videoPath, window.location.origin).href);
              return;
            }
            if (!cancelled) setUrlError('Could not load video from storage. Check login and that the file exists.');
            return;
          }
        }
      }
      if (payload.videoPath) {
        if (!cancelled) setResolvedUrl(new URL(payload.videoPath, window.location.origin).href);
      } else {
        if (!cancelled) setUrlError('No video URL');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload, mergedTour?.videoStoragePath]);

  useEffect(() => {
    syncRef.current = {
      isHost,
      isStudent,
      classSessionId,
      userId: user?.uid ?? null,
    };
  }, [isHost, isStudent, classSessionId, user?.uid]);

  useEffect(() => {
    (window as unknown as { __krpanoOnViewChange?: (h: number, v: number, f: number) => void }).__krpanoOnViewChange = (
      h: number,
      v: number,
      f: number
    ) => {
      const r = syncRef.current;
      if (r.isStudent && r.classSessionId && r.userId) {
        const t = Date.now();
        if (t - lastStudentReport.current < 150) return;
        lastStudentReport.current = t;
        void reportStudentView(r.classSessionId, r.userId, { hlookat: h, vlookat: v, fov: f });
      }
      if (r.isHost && r.classSessionId && r.userId) {
        const t = Date.now();
        if (t - lastTeacherSend.current < TEACHER_VIEW_MS) return;
        lastTeacherSend.current = t;
        setHostLookat({ hlookat: h, vlookat: v, fov: f, playing: playingRef.current });
        // Continuous drag — no force/sync_id
        void updateTeacherView(r.classSessionId, r.userId, { hlookat: h, vlookat: v, fov: f });
      }
    };
    return () => {
      (window as unknown as { __krpanoOnViewChange?: unknown }).__krpanoOnViewChange = undefined;
    };
  }, []);

  useEffect(() => {
    if (!reportEnabled || !classSessionId || !user?.uid) return;
    const name = profile?.displayName || profile?.name || user.email || user.uid;
    const email = user.email || null;
    void reportSessionProgress(classSessionId, user.uid, name, 'exploration', null, undefined, email);
    const id = window.setInterval(() => {
      void reportSessionProgress(classSessionId, user.uid, name, 'exploration', null, undefined, email);
    }, 60000);
    return () => clearInterval(id);
  }, [reportEnabled, classSessionId, user?.uid, profile?.displayName, profile?.name, user?.email]);

  // Student: follow teacher_view (orientation + optional video playhead on force Direct)
  const teacherView = sessionSnap?.teacher_view;
  useEffect(() => {
    if (!isStudent || !teacherView) return;
    const k = krpanoRef.current;
    if (!k?.call) return;
    const h = Number(teacherView.hlookat);
    const v = Number(teacherView.vlookat);
    const f = teacherView.fov != null ? Number(teacherView.fov) : 80;
    const syncId =
      typeof teacherView.sync_id === 'number' && Number.isFinite(teacherView.sync_id)
        ? teacherView.sync_id
        : null;
    if (!Number.isFinite(h) || !Number.isFinite(v)) return;
    const prev = lastAppliedTeacherView.current;
    const isNewDirect = syncId != null && syncId !== prev?.syncId;
    if (prev && prev.h === h && prev.v === v && prev.fov === f && !isNewDirect) {
      return;
    }
    lastAppliedTeacherView.current = { h, v, fov: f, syncId };
    applyTeacherViewToKrpano(
      k,
      { hlookat: h, vlookat: v, fov: f, sync_id: syncId ?? undefined },
      { force: isNewDirect }
    );
    // Playback state only on explicit Direct (new sync_id) to avoid fighting local pause/play
    if (isNewDirect) {
      if (typeof teacherView.video_time === 'number' && Number.isFinite(teacherView.video_time)) {
        seekKrpanoVideo(k, teacherView.video_time, typeof teacherView.playing === 'boolean' ? teacherView.playing : undefined);
      } else if (typeof teacherView.playing === 'boolean') {
        seekKrpanoVideo(k, readKrpanoVideoTime(k) ?? 0, teacherView.playing);
      }
      if (typeof teacherView.playing === 'boolean') setPlaying(teacherView.playing);
    }
  }, [
    isStudent,
    teacherView?.hlookat,
    teacherView?.vlookat,
    teacherView?.fov,
    teacherView?.sync_id,
    teacherView?.video_time,
    teacherView?.playing,
    engine,
  ]);

  // Sync WebXR / krpano VR state so the toolbar can toggle Enter / Exit
  useEffect(() => {
    if (engine !== 'krpano') {
      setInKrpanoVr(false);
      return;
    }
    let cancelled = false;
    const poll = () => {
      if (cancelled) return;
      const viewer = krpanoRef.current;
      if (viewer?.get) {
        try {
          const flag = viewer.get('webvr.isenabled');
          const enabled = flag === true || flag === 'true' || flag === 1;
          setInKrpanoVr(enabled);
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setTimeout(poll, 500);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [engine]);

  const handleEnterVr = useCallback(() => {
    const k = krpanoRef.current;
    if (!k?.call) return;
    try {
      k.call('webvr.enterVR');
    } catch (e) {
      console.warn('[VR360] webvr.enterVR failed:', e);
    }
  }, []);

  const handleExitVr = useCallback(() => {
    const k = krpanoRef.current;
    if (!k?.call) return;
    try {
      k.call('webvr.exitVR');
    } catch (e) {
      console.warn('[VR360] webvr.exitVR failed:', e);
    }
  }, []);

  const onBack = useCallback(() => {
    navigate('/lessons', { replace: true });
  }, [navigate]);

  const getLiveHostView = useCallback((): HostLookat | null => {
    return readLiveLookat(krpanoRef.current, playingRef.current) || hostLookat;
  }, [hostLookat]);

  // Keep krpano video transport in sync with toolbar Play/Pause + Mute
  useEffect(() => {
    if (engine !== 'krpano') return;
    const k = krpanoRef.current;
    if (!k?.call) return;
    try {
      k.call(playing ? 'plugin[video].play();' : 'plugin[video].pause();');
    } catch {
      /* ignore */
    }
  }, [playing, engine]);

  useEffect(() => {
    if (engine !== 'krpano') return;
    const k = krpanoRef.current;
    if (!k?.call) return;
    try {
      k.call(`set(plugin[video].muted,${muted ? 'true' : 'false'});`);
    } catch {
      /* ignore */
    }
  }, [muted, engine]);

  // krpano embed
  useEffect(() => {
    if (!resolvedUrl) return;
    if (effectivePlayer === 'three') {
      setEngine('three');
      return;
    }

    krpanoRef.current = null;
    setEngine('pending');
    lastAppliedTeacherView.current = null;

    let removed = false;
    const run = async () => {
      try {
        await loadKrpanoScript();
        if (removed) return;
        const container = document.getElementById(KRPANO_TARGET_ID);
        if (!container) return;
        container.innerHTML = '';
        const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
        const xml = buildKrpano360VideoXml({
          videoUrl: resolvedUrl,
          basePath: '/krpano/',
          origin,
          hlookat: 0,
          vlookat: 0,
          fov: 90,
          webvr: true,
        });
        embedKrpano({
          xml,
          target: KRPANO_TARGET_ID,
          basepath: '/krpano/',
          onready: (k) => {
            if (removed) return;
            krpanoRef.current = k as KrpanoApi;
            setEngine('krpano');
            try {
              (k as KrpanoApi).call('sync_view_to_js');
            } catch {
              /* optional */
            }
            setTimeout(() => {
              if (!krpanoRef.current) return;
              try {
                krpanoRef.current.call('sync_view_to_js');
              } catch {
                /* ignore */
              }
            }, 500);
          },
          onerror: (msg) => {
            console.warn('VR360 krpano error:', msg);
            if (!removed) setEngine('three');
          },
        });
      } catch (e) {
        console.warn('VR360 krpano load failed', e);
        if (!removed) setEngine('three');
      }
    };
    void run();
    return () => {
      removed = true;
      krpanoRef.current = null;
      const c = document.getElementById(KRPANO_TARGET_ID);
      if (c) c.innerHTML = '';
    };
  }, [resolvedUrl, effectivePlayer]);

  const threeUrl = useMemo(() => (resolvedUrl ? resolvedUrl : null), [resolvedUrl]);

  if (loadError || !payload) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4 p-6 z-50">
        <p className="text-foreground text-center max-w-md">{loadError || 'Loading…'}</p>
        <Button onClick={() => navigate('/lessons')}>Back to Lessons</Button>
      </div>
    );
  }

  if (urlError && !resolvedUrl) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4 p-6 z-50">
        <p className="text-foreground text-center max-w-md">{urlError}</p>
        <Button onClick={() => navigate('/lessons')}>Back to Lessons</Button>
      </div>
    );
  }

  const showKrpano = effectivePlayer !== 'three' && (engine === 'krpano' || engine === 'pending');
  const showThree = engine === 'three' || effectivePlayer === 'three';
  const hostSessionCode = sessionSnap?.session_code || activeSession?.session_code || null;

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/*
        pointer-events-none on the bar so krpano’s own “Enter VR” layer receives clicks; children use pointer-events-auto.
      */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between gap-2 p-3 sm:p-4 bg-gradient-to-b from-black/75 via-black/35 to-transparent pointer-events-none">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          className="gap-2 pointer-events-auto shrink-0 border-white/10 bg-zinc-950/70 text-white hover:bg-zinc-900/80"
          type="button"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden xs:inline sm:inline">Back</span>
        </Button>
        <div className="flex min-w-0 max-w-[46%] items-center gap-2 truncate text-sm text-white/90 select-none">
          <Video className="h-4 w-4 shrink-0 text-teal-300/90" />
          <span className="truncate font-medium tracking-tight">{payload.title}</span>
        </div>
        <div className="flex items-center gap-1.5 pointer-events-auto shrink-0">
          {showKrpano && engine === 'krpano' && (
            <Button
              variant={inKrpanoVr ? 'secondary' : 'default'}
              size="sm"
              className="gap-1.5 border-white/10"
              type="button"
              onClick={inKrpanoVr ? handleExitVr : handleEnterVr}
              aria-pressed={inKrpanoVr}
              aria-label={inKrpanoVr ? 'Exit VR' : 'Enter VR'}
            >
              {inKrpanoVr ? <X className="h-4 w-4" /> : <Glasses className="h-4 w-4" />}
              <span className="hidden sm:inline">{inKrpanoVr ? 'Exit VR' : 'Enter VR'}</span>
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setMuted((m) => !m)}
            className="gap-1 border-white/10 bg-zinc-950/70 text-white hover:bg-zinc-900/80"
            type="button"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPlaying((p) => !p)}
            className="gap-1 border-white/10 bg-zinc-950/70 text-white hover:bg-zinc-900/80"
            type="button"
          >
            {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            <span className="hidden sm:inline">{playing ? 'Pause' : 'Play'}</span>
          </Button>
        </div>
      </div>

      {showHostOverlay && (
        <>
          {/* Compact host strip. The old floating control stack was removed when
              LiveClassHostOverlay was reduced to just its drawer; this keeps the
              two controls this player actually used. */}
          <div className="pointer-events-auto absolute right-3 top-3 z-hud flex items-center gap-1.5 rounded-xl border border-white/12 bg-zinc-950/80 p-1.5 backdrop-blur-2xl">
            <button
              type="button"
              onClick={() => setTourDrawer((d) => (d === 'roster' ? null : 'roster'))}
              title="Student roster"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.06] px-2.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
            >
              Roster <span className="tabular-nums">{progressList.length}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const v = getLiveHostView?.() || hostLookat || sessionSnap?.teacher_view || null;
                if (!v || !classSessionId || !user?.uid) return;
                void updateTeacherView(classSessionId, user.uid, {
                  hlookat: v.hlookat,
                  vlookat: v.vlookat,
                  fov: v.fov,
                  force: true,
                  sync_id: Date.now(),
                });
              }}
              title="Point the whole class at what you are looking at"
              className="inline-flex h-9 items-center rounded-lg border border-white/12 bg-white/[0.06] px-2.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
            >
              Direct view
            </button>
          </div>
          <LiveClassHostOverlay
            session={sessionSnap || activeSession}
            sessionId={classSessionId}
            hostUid={user?.uid ?? null}
            progressList={progressList}
            sessionCode={hostSessionCode}
            openDrawer={tourDrawer}
            onDrawerChange={setTourDrawer}
          />
        </>
      )}

      {showKrpano && (
        <>
          <div id={KRPANO_TARGET_ID} className="h-full w-full" style={{ minHeight: '100vh' }} />
          {engine === 'pending' && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm pointer-events-none">
              Starting 360° player…
            </div>
          )}
        </>
      )}

      {showThree && threeUrl && (
        <Canvas
          className="h-full w-full"
          gl={{ antialias: true, alpha: false }}
          camera={{ position: [0, 0, 0.1], fov: 80, far: 2000, near: 0.1 }}
        >
          <color attach="background" args={['#050810']} />
          <Suspense fallback={null}>
            <VideoSphere url={threeUrl} muted={muted} playing={playing} />
          </Suspense>
          <OrbitControls enableZoom enablePan={false} rotateSpeed={-0.5} />
          {reportEnabled && classSessionId && user?.uid && (
            <ViewSyncReporter sessionId={classSessionId} userUid={user.uid} enabled />
          )}
        </Canvas>
      )}
    </div>
  );
};

export default VR360VideoTourPlayer;
